import React from 'react';
import { motion } from 'framer-motion';
import { FileText, FileSignature, Server, TrendingUp, AlertTriangle, CheckCircle, Clock, LayoutDashboard } from 'lucide-react';
import Card3D from '../admindashboard/Card3D.js';

const StatCard = ({ icon: Icon, label, value, accent, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Card3D className="content-glass rounded-2xl p-4" intensity={5} lift={10}>
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-xl border flex items-center justify-center flex-shrink-0"
            style={{
              background: accent,
              borderColor: 'var(--nad-b2)',
            }}
          >
            <Icon size={20} style={{ color: 'var(--nad-accent-txt)' }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--nad-t4)' }}>
              {label}
            </p>
            <p className="text-2xl font-bold mt-1" style={{ color: 'var(--nad-t1)' }}>
              {value}
            </p>
          </div>
        </div>
      </Card3D>
    </motion.div>
  );
};

const ServiceLifecycleDashboard = () => {
  const accentYellow = 'linear-gradient(to bottom right, rgba(250,204,21,0.22), rgba(250,204,21,0.08))';
  const accentBlue = 'linear-gradient(to bottom right, rgba(59,130,246,0.22), rgba(59,130,246,0.08))';
  const accentGreen = 'linear-gradient(to bottom right, rgba(34,197,94,0.22), rgba(34,197,94,0.08))';
  const accentAmber = 'linear-gradient(to bottom right, rgba(245,158,11,0.22), rgba(245,158,11,0.08))';

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <Card3D className="content-glass rounded-2xl p-5" intensity={4} lift={8}>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl border flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(to bottom right, rgba(59,130,246,0.22), rgba(250,204,21,0.18))',
                borderColor: 'var(--nad-b2)',
              }}
            >
              <LayoutDashboard size={20} style={{ color: 'var(--nad-accent-txt)' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ color: 'var(--nad-t1)' }}>
                Service Overview
              </h2>
              <p className="text-xs" style={{ color: 'var(--nad-t3)' }}>
                High-level snapshot of field service performance, quotation pipeline, and deployed machine health
              </p>
            </div>
          </div>
        </Card3D>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Total FSRs" value="0" accent={accentYellow} delay={0.05} />
        <StatCard icon={FileSignature} label="Quotations" value="0" accent={accentBlue} delay={0.10} />
        <StatCard icon={Server} label="Machines in Field" value="0" accent={accentGreen} delay={0.15} />
        <StatCard icon={TrendingUp} label="Completed This Month" value="0" accent={accentAmber} delay={0.20} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-2"
        >
          <Card3D className="content-glass rounded-2xl p-5" intensity={4} lift={8}>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-9 h-9 rounded-xl border flex items-center justify-center"
                style={{
                  background:
                    'linear-gradient(to bottom right, rgba(59,130,246,0.20), rgba(250,204,21,0.16))',
                  borderColor: 'var(--nad-b2)',
                }}
              >
                <AlertTriangle size={18} style={{ color: 'var(--nad-accent-txt)' }} />
              </div>
              <div>
                <h3 className="text-base font-bold" style={{ color: 'var(--nad-t1)' }}>
                  Recent Service Activity
                </h3>
                <p className="text-xs" style={{ color: 'var(--nad-t3)' }}>
                  Latest updates from field service operations
                </p>
              </div>
            </div>
            <div
              className="rounded-xl p-6 text-center border"
              style={{
                background: 'var(--nad-s1)',
                borderColor: 'var(--nad-b1)',
              }}
            >
              <Clock size={28} className="mx-auto mb-2" style={{ color: 'var(--nad-t4)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--nad-t2)' }}>
                Awaiting data connection
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--nad-t4)' }}>
                Service records will appear here once loaded.
              </p>
            </div>
          </Card3D>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.30, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card3D className="content-glass rounded-2xl p-5 h-full" intensity={4} lift={8}>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-9 h-9 rounded-xl border flex items-center justify-center"
                style={{
                  background:
                    'linear-gradient(to bottom right, rgba(34,197,94,0.20), rgba(59,130,246,0.14))',
                  borderColor: 'var(--nad-b2)',
                }}
              >
                <CheckCircle size={18} style={{ color: 'var(--nad-accent-txt)' }} />
              </div>
              <div>
                <h3 className="text-base font-bold" style={{ color: 'var(--nad-t1)' }}>
                  Workflow Status
                </h3>
                <p className="text-xs" style={{ color: 'var(--nad-t3)' }}>
                  Current pipeline snapshot
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { label: 'Pending FSRs', value: 0, color: 'rgba(250,204,21,0.60)' },
                { label: 'Open Quotations', value: 0, color: 'rgba(59,130,246,0.60)' },
                { label: 'Warranty Due Soon', value: 0, color: 'rgba(248,113,113,0.60)' },
                { label: 'Preventive Maintenance', value: 0, color: 'rgba(34,197,94,0.60)' },
              ].map((item, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-medium" style={{ color: 'var(--nad-t3)' }}>
                      {item.label}
                    </span>
                    <span className="text-[11px] font-bold" style={{ color: item.color }}>
                      {item.value}
                    </span>
                  </div>
                  <div
                    className="h-1.5 w-full rounded-full overflow-hidden"
                    style={{ background: 'var(--nad-s2)' }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: '0%', background: item.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card3D>
        </motion.div>
      </div>
    </div>
  );
};

export default ServiceLifecycleDashboard;
